/**
 * ProfileImageUpload (iter79)
 *
 * Reusable avatar upload — accepts JPG/PNG/WebP up to 8MB, POSTs to
 * /api/me/profile-image, and shows an inline preview using the auth-gated
 * /api/files/{path}?auth=<token> URL so browser <img> can render it.
 *
 * `onUploaded(path)` fires after a successful upload so callers can
 * refresh the user object / bump form state.
 */
import { useEffect, useRef, useState } from "react";
import { Camera, CheckCircle2, Loader, Trash2, Upload, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { checkImageQuality } from "@/lib/imageQualityCheck";

export function buildAvatarUrl(path) {
  if (!path) return null;
  // Auth key matches api.js ACCESS_KEY = "rl_token". Try localStorage first
  // (remember-me users), fall back to sessionStorage.
  let token = "";
  try {
    token = localStorage.getItem("rl_token") || sessionStorage.getItem("rl_token") || "";
  } catch { /* ignore */ }
  const backend = process.env.REACT_APP_BACKEND_URL || "";
  return `${backend}/api/files/${path}?auth=${encodeURIComponent(token)}`;
}

export default function ProfileImageUpload({ onUploaded, size = 96 }) {
  const { user, setUser } = useAuth();
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(user?.avatar_path || null);
  const [status, setStatus] = useState(user?.avatar_status || null);
  // Client-side quality hint from the last-picked file. `null` = not run yet.
  const [qualityHint, setQualityHint] = useState(null);
  const inputRef = useRef(null);
  const cameraInputRef = useRef(null);

  useEffect(() => { setPreview(user?.avatar_path || null); }, [user?.avatar_path]);
  useEffect(() => { setStatus(user?.avatar_status || null); }, [user?.avatar_status]);

  const pick = () => inputRef.current?.click();
  // `capture="user"` on the input opens the front camera directly on mobile
  // Safari/Chrome. Desktop browsers silently ignore the attribute and fall
  // back to the file picker, so this is safe to render everywhere.
  const takePhoto = () => cameraInputRef.current?.click();

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image too large (max 8MB).");
      return;
    }
    // Instant client-side quality hint. Advisory only — a `try_again`
    // verdict shows the chip but does NOT block upload; the AI backstop is
    // authoritative. This just saves a round-trip for obvious problems.
    let hint;
    try {
      hint = await checkImageQuality(file);
    } catch {
      hint = { verdict: "good", reason: "" };
    }
    setQualityHint(hint);
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/me/profile-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 90000,
      });
      if (data.status === "approved") {
        setPreview(data.avatar_path);
        setStatus("approved");
        setQualityHint(null);  // hide the chip once the photo is live
        if (setUser && user) {
          setUser({ ...user, avatar_path: data.avatar_path, avatar_status: "approved", avatar_updated_at: new Date().toISOString() });
        }
        onUploaded && onUploaded(data.avatar_path);
        toast.success("Profile photo saved");
      } else {
        // pending_review — photo lives on the server but isn't public yet.
        setPreview(null);
        setStatus("pending_review");
        if (setUser && user) {
          const { avatar_path: _ap, ...rest } = user;
          setUser({ ...rest, avatar_status: "pending_review", avatar_updated_at: new Date().toISOString() });
        }
        onUploaded && onUploaded(null);
        toast.message("Awaiting quick review", {
          description: data.reason || "An admin will approve your photo shortly. Your initials will show meanwhile.",
        });
      }
    } catch (err) {
      // 400 with an AI reason = rejected upload; show the reason.
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
      if (cameraInputRef.current) cameraInputRef.current.value = "";
    }
  };

  const removePhoto = async () => {
    if (!window.confirm("Remove your profile photo? Your initials will show instead.")) return;
    setBusy(true);
    try {
      await api.delete("/me/profile-image");
      setPreview(null);
      setStatus(null);
      setQualityHint(null);
      if (setUser && user) {
        const { avatar_path: _ap, avatar_updated_at: _au, avatar_status: _as, ...rest } = user;
        setUser(rest);
      }
      onUploaded && onUploaded(null);
      toast.success("Profile photo removed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not remove photo");
    } finally {
      setBusy(false);
    }
  };

  const avatarUrl = buildAvatarUrl(preview);
  const isPending = status === "pending_review" && !avatarUrl;

  return (
    <div className="flex items-start gap-4 flex-wrap" data-testid="profile-image-upload">
      <div
        className="relative rounded-full overflow-hidden border border-[#262626] bg-[#141414] flex items-center justify-center"
        style={{ width: size, height: size }}
      >
        {avatarUrl ? (
          <img
            src={avatarUrl} alt="Profile"
            className="w-full h-full object-cover"
            data-testid="profile-image-preview"
          />
        ) : (
          <Camera className="w-6 h-6 text-[#525252]" strokeWidth={1.5} />
        )}
        {busy && (
          <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
            <Loader className="w-4 h-4 animate-spin text-[#CCFF00]" />
          </div>
        )}
      </div>
      <div className="flex flex-col gap-2 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button" onClick={pick} disabled={busy}
            data-testid="profile-image-pick"
            className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider border border-[#262626] px-3 py-2 hover:border-[#CCFF00] hover:text-[#CCFF00]"
          >
            <Upload className="w-3 h-3" strokeWidth={2} />
            {avatarUrl ? "Change photo" : (isPending ? "Try another photo" : "Upload photo")}
          </button>
          <button
            type="button" onClick={takePhoto} disabled={busy}
            data-testid="profile-image-camera"
            className="sm:hidden inline-flex items-center gap-1.5 text-xs uppercase tracking-wider border border-[#262626] px-3 py-2 hover:border-[#CCFF00] hover:text-[#CCFF00]"
            title="Open camera to take a photo now"
          >
            <Camera className="w-3 h-3" strokeWidth={2} />
            Take photo
          </button>
          {avatarUrl && (
            <button
              type="button" onClick={removePhoto} disabled={busy}
              data-testid="profile-image-remove"
              className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider border border-[#262626] px-3 py-2 text-[#A3A3A3] hover:border-[#FF4500] hover:text-[#FF4500] disabled:opacity-40"
            >
              <Trash2 className="w-3 h-3" strokeWidth={2} />
              Remove
            </button>
          )}
        </div>
        {isPending && (
          <div
            data-testid="profile-image-pending-badge"
            className="text-[10px] uppercase tracking-widest text-[#CCFF00] border border-[#CCFF00] px-2 py-1 inline-flex items-center gap-1.5 self-start"
          >
            <Loader className="w-3 h-3 animate-spin" strokeWidth={2} />
            Awaiting quick review — your initials show meanwhile
          </div>
        )}
        {qualityHint && qualityHint.verdict === "good" && !isPending && !busy && (
          <div
            data-testid="profile-image-quality-good"
            className="text-[10px] uppercase tracking-widest text-[#CCFF00] border border-[#CCFF00] px-2 py-1 inline-flex items-center gap-1.5 self-start"
          >
            <CheckCircle2 className="w-3 h-3" strokeWidth={2} />
            Looks good
          </div>
        )}
        {qualityHint && qualityHint.verdict === "try_again" && !busy && (
          <div
            data-testid="profile-image-quality-warn"
            className="text-[10px] uppercase tracking-widest text-[#FFB020] border border-[#FFB020] px-2 py-1 inline-flex items-start gap-1.5 self-start max-w-xs leading-snug"
            title={qualityHint.reason}
          >
            <AlertTriangle className="w-3 h-3 shrink-0 mt-[1px]" strokeWidth={2} />
            <span>
              Try a clearer shot
              {qualityHint.reason ? <span className="block normal-case tracking-normal text-[10px] text-[#A3A3A3] mt-0.5">{qualityHint.reason}</span> : null}
            </span>
          </div>
        )}
      </div>
      <input
        ref={inputRef} type="file"
        accept="image/png,image/jpeg,image/webp"
        onChange={upload} className="hidden"
        data-testid="profile-image-input"
      />
      <input
        ref={cameraInputRef} type="file"
        accept="image/png,image/jpeg,image/webp"
        capture="user"
        onChange={upload} className="hidden"
        data-testid="profile-image-camera-input"
      />
    </div>
  );
}
